import { MXBaseComponent, AXComponent, AXClosbaleComponent } from '@acorex/components/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, EventEmitter, Output, Input, HostBinding, NgModule } from '@angular/core';
import { isBrowser } from '@acorex/core/platform';
import { CommonModule } from '@angular/common';

class AXDrawerContainerComponent extends MXBaseComponent {
    ngDoCheck() {
        const host = this.getHostElement();
        Array.from(host.querySelectorAll('ax-drawer'))
            .map((c) => c['__axContext__'])
            .some((c) => !c?.collapsed)
            ? host.classList.add('ax-visible')
            : host.classList.remove('ax-visible');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerContainerComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXDrawerContainerComponent, selector: "ax-drawer-container", usesInheritance: true, ngImport: i0, template: `
    <ng-content select="ax-drawer[location='start']"> </ng-content>
    <ng-content select="ax-content"> </ng-content>
    <ng-content select="ax-drawer[location='end']"> </ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ax-drawer-container',
                    template: `
    <ng-content select="ax-drawer[location='start']"> </ng-content>
    <ng-content select="ax-content"> </ng-content>
    <ng-content select="ax-drawer[location='end']"> </ng-content>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    encapsulation: ViewEncapsulation.None,
                }]
        }] });

class AXDrawerComponent extends MXBaseComponent {
    constructor(_zone) {
        super();
        this._zone = _zone;
        this.locationChange = new EventEmitter();
        this._location = 'start';
        this._showBackdrop = true;
        this.modeChange = new EventEmitter();
        this._mode = 'overlay';
        this.collapsedChange = new EventEmitter();
        this._collapsed = true;
        this._loaded = false;
    }
    get location() {
        return this._location;
    }
    set location(v) {
        this.setOption({
            name: 'location',
            value: v,
        });
    }
    get showBackdrop() {
        return this._showBackdrop;
    }
    set showBackdrop(v) {
        this.setOption({
            name: 'showBackdrop',
            value: v,
        });
    }
    get mode() {
        return this._mode;
    }
    set mode(v) {
        this.setOption({
            name: 'mode',
            value: v,
        });
    }
    get collapsed() {
        return this._collapsed;
    }
    set collapsed(v) {
        this.setOption({
            name: 'collapsed',
            value: v,
        });
    }
    ngAfterContentInit() {
        this._detectBoundingSize();
        this._loaded = true;
    }
    internalOptionChanged(option) {
        this._checkProps();
    }
    ngDoCheck() {
        this._detectBoundingSize();
    }
    _detectBoundingSize() {
        this._zone.runOutsideAngular(() => {
            const host = this.getHostElement();
            if (host.clientWidth) {
                host.style.setProperty('--ax-el-width', `${host.clientWidth}px`);
            }
        });
    }
    _checkProps() {
        this._detectBoundingSize();
        this._removeBackdrop();
        if (this.mode == 'overlay' && !this.collapsed && this.showBackdrop) {
            this._addBackdrop();
        }
    }
    toggle() {
        this.collapsed = !this.collapsed;
    }
    close() {
        this.collapsed = true;
    }
    open() {
        this.collapsed = false;
    }
    get __hostClass() {
        return `ax-drawer-${this.mode} ax-drawer-${this.location} ${this.collapsed ? 'ax-collapsed' : 'ax-expanded'} ${this._loaded ? 'ax-state-loaded' : ''}`;
    }
    _addBackdrop() {
        if (isBrowser()) {
            this._zone.runOutsideAngular(() => {
                this._backdropElement = document.createElement('div');
                this._backdropElement.classList.add('ax-backdrop');
                this._backdropElement.onclick = () => {
                    this._zone.run(() => {
                        this.close();
                    });
                };
                this.getHostElement()?.parentElement?.appendChild(this._backdropElement);
            });
        }
    }
    _removeBackdrop() {
        if (isBrowser()) {
            this._zone.runOutsideAngular(() => {
                if (this._backdropElement) {
                    this.getHostElement()?.parentElement?.removeChild(this._backdropElement);
                    this._backdropElement = null;
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXDrawerComponent, selector: "ax-drawer", inputs: { location: "location", showBackdrop: "showBackdrop", mode: "mode", collapsed: "collapsed" }, outputs: { locationChange: "locationChange", modeChange: "modeChange", collapsedChange: "collapsedChange" }, host: { properties: { "class": "this.__hostClass" } }, providers: [
            { provide: AXComponent, useExisting: AXDrawerComponent },
            { provide: AXClosbaleComponent, useExisting: AXDrawerComponent },
        ], usesInheritance: true, ngImport: i0, template: `
    <ng-content select="ax-header"></ng-content>
    <ng-content select="ax-content"></ng-content>
  `, isInline: true, styles: ["ax-drawer-container{position:relative;display:flex;height:100%;width:100%;overflow:hidden}ax-drawer-container.ax-fixed{bottom:0;inset-inline-end:0px;inset-inline-start:0px;top:0;z-index:50;display:none}ax-drawer-container.ax-fixed.ax-visible{display:flex}ax-drawer-container .ax-backdrop{position:absolute;bottom:0;top:0;z-index:10;height:100%;width:100%;background-color:#0003;transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-drawer-container ax-drawer{display:flex;height:100%;max-width:95%;flex-direction:column;background-color:rgba(var(--ax-color-surface))}ax-drawer-container ax-drawer.ax-state-loaded{transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-drawer-container ax-drawer ax-header,ax-drawer-container ax-drawer ax-footer{padding:.75rem}ax-drawer-container ax-drawer ax-footer{border-top-width:1px;border-color:rgba(var(--ax-color-border-default))}ax-drawer-container ax-drawer ax-header{border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default))}ax-drawer-container ax-drawer>ax-content{flex:1 1 0%;overflow:auto}ax-drawer-container ax-drawer.ax-drawer-overlay{position:absolute}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-expanded{z-index:20}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-collapsed{z-index:0}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-start.ax-expanded{inset-inline-start:0px}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-start.ax-collapsed{inset-inline-start:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-end.ax-expanded{inset-inline-end:0px}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-end.ax-collapsed{inset-inline-end:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-push{flex:0 1 auto}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start{order:-9999}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start.ax-expanded{margin-inline-start:0px}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start.ax-collapsed{margin-inline-start:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end{order:9999}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end.ax-expanded{margin-inline-end:0px}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end.ax-collapsed{margin-inline-end:calc(-1 * var(--ax-el-width))}ax-drawer-container>ax-content{height:100%;flex:1 1 0%;overflow:auto}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-drawer', template: `
    <ng-content select="ax-header"></ng-content>
    <ng-content select="ax-content"></ng-content>
  `, changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        { provide: AXComponent, useExisting: AXDrawerComponent },
                        { provide: AXClosbaleComponent, useExisting: AXDrawerComponent },
                    ], styles: ["ax-drawer-container{position:relative;display:flex;height:100%;width:100%;overflow:hidden}ax-drawer-container.ax-fixed{bottom:0;inset-inline-end:0px;inset-inline-start:0px;top:0;z-index:50;display:none}ax-drawer-container.ax-fixed.ax-visible{display:flex}ax-drawer-container .ax-backdrop{position:absolute;bottom:0;top:0;z-index:10;height:100%;width:100%;background-color:#0003;transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-drawer-container ax-drawer{display:flex;height:100%;max-width:95%;flex-direction:column;background-color:rgba(var(--ax-color-surface))}ax-drawer-container ax-drawer.ax-state-loaded{transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-drawer-container ax-drawer ax-header,ax-drawer-container ax-drawer ax-footer{padding:.75rem}ax-drawer-container ax-drawer ax-footer{border-top-width:1px;border-color:rgba(var(--ax-color-border-default))}ax-drawer-container ax-drawer ax-header{border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default))}ax-drawer-container ax-drawer>ax-content{flex:1 1 0%;overflow:auto}ax-drawer-container ax-drawer.ax-drawer-overlay{position:absolute}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-expanded{z-index:20}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-collapsed{z-index:0}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-start.ax-expanded{inset-inline-start:0px}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-start.ax-collapsed{inset-inline-start:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-end.ax-expanded{inset-inline-end:0px}ax-drawer-container ax-drawer.ax-drawer-overlay.ax-drawer-end.ax-collapsed{inset-inline-end:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-push{flex:0 1 auto}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start{order:-9999}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start.ax-expanded{margin-inline-start:0px}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-start.ax-collapsed{margin-inline-start:calc(-1 * var(--ax-el-width))}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end{order:9999}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end.ax-expanded{margin-inline-end:0px}ax-drawer-container ax-drawer.ax-drawer-push.ax-drawer-end.ax-collapsed{margin-inline-end:calc(-1 * var(--ax-el-width))}ax-drawer-container>ax-content{height:100%;flex:1 1 0%;overflow:auto}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }], propDecorators: { locationChange: [{
                type: Output
            }], location: [{
                type: Input
            }], showBackdrop: [{
                type: Input
            }], modeChange: [{
                type: Output
            }], mode: [{
                type: Input
            }], collapsedChange: [{
                type: Output
            }], collapsed: [{
                type: Input
            }], __hostClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

const COMPONENT = [AXDrawerComponent, AXDrawerContainerComponent];
const MODULES = [CommonModule];
class AXDrawerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerModule, declarations: [AXDrawerComponent, AXDrawerContainerComponent], imports: [CommonModule], exports: [AXDrawerComponent, AXDrawerContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerModule, imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDrawerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENT],
                    imports: [...MODULES],
                    exports: [...COMPONENT],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXDrawerComponent, AXDrawerContainerComponent, AXDrawerModule };
